import { EmbeddedCodeLike, ItemFinanceApproval } from '../../../../../entities/shared/code';
import { InvoiceStatus } from '../../../../../entities/invoicing/invoicing.enum';
import { FinanceMarkSide, ItemFinanceLock } from '../../../../../entities/job/finance.marks';

/**
 * A field name on a job item. Every security field list is typed with it, so a rename on the model
 * fails at compile time instead of silently dropping that field's guard.
 *
 * `EmbeddedCodeLike`, not `EmbeddedCode`: `itemStatus` lives on `PendingCode`.
 */
export type ItemFieldKey = keyof EmbeddedCodeLike;

/** Fields whose write needs `canModifyPricing`. */
export const itemPricingFields: ItemFieldKey[] = ['buyPrice', 'sellPrice', 'customBuyPrice', 'customSellPrice', 'adjustmentType', 'adjustment', 'adjustedSell'];

/**
 * The fields every approved document holds: it was raised from the line's shape, so changing them
 * would leave the document describing a line that no longer exists.
 */
export const lockedItemFields: ItemFieldKey[] = ['quantity', 'codeUoM', 'status', 'itemStatus'];

/** The sell side an approved invoice carries. */
export const invoiceLockedFields: ItemFieldKey[] = ['codeInvoicingStatus', 'sellPrice', 'customSellPrice', 'adjustmentType', 'adjustment', 'adjustedSell'];

/**
 * The buy side an approved bill carries. `payable` included: it zeroes the buy price the bill was
 * raised from.
 */
export const billLockedFields: ItemFieldKey[] = ['suppliedBySubcontractor', 'payable', 'buyPrice', 'customBuyPrice'];

/**
 * What an Approved purchase order fixes: the quantity ordered and the price it was ordered at. The
 * legacy dialog locked exactly these three; sell, status, billable and payable stay the job's.
 */
export const purchaseOrderLockedFields: ItemFieldKey[] = ['quantity', 'codeUoM', 'buyPrice', 'customBuyPrice'];

/**
 * A document in one of these statuses locks the line it carries. Error sits with Approved because an
 * Error document has been sent to the accounting system and failed there; its lines are still spoken
 * for until it is fixed or voided.
 */
export function isFinanceDocumentLocking(status: InvoiceStatus | null | undefined): boolean {

    return status === InvoiceStatus.Approved || status === InvoiceStatus.Error;
}

/**
 * Fields whose write needs `canModifyLockedItems` on this item, by the side each approved document
 * owns (WBS-12127): an approved invoice locks the sell side, an approved bill the buy side, and either
 * locks the line's shape. This is the legacy Items tab's rule and its write path's, so a billed line
 * keeps its sell price editable on the job, and a Draft document locks nothing yet. An Approved purchase
 * order fixes only what it ordered: the line sync writes back to Draft orders alone, so an Approved one
 * would otherwise drift from the line.
 */
export function lockedItemFieldsFor(item: ItemFinanceApproval | null | undefined): ItemFieldKey[] {

    const sellSide = item?.invoiceApproved === true;
    const buySide = item?.billApproved === true;
    const ordered = item?.purchaseOrderApproved === true;

    if (!sellSide && !buySide && !ordered) return [];

    return [...new Set([
        ...(sellSide || buySide ? lockedItemFields : []),
        ...(sellSide ? invoiceLockedFields : []),
        ...(buySide ? billLockedFields : []),
        ...(ordered ? purchaseOrderLockedFields : []),
    ])];
}

/**
 * The three fields each finance mark forces. A patch to any of them is refused while that side is
 * marked, and the client reads the same lists, so an editor cannot offer a field the write refuses.
 */
export const financeLockedFields: Record<FinanceMarkSide, ItemFieldKey[]> = {
    billable: ['codeInvoicingStatus', 'sellPrice', 'customSellPrice'],
    payable: ['payable', 'buyPrice', 'customBuyPrice'],
};

/**
 * Did the caller actually change this locked field?
 *
 * WBS-10631: the item editor posts the whole item back, not just the fields the user touched, so the
 * two sides have to be compared by meaning rather than by representation. A field the document never
 * carried arrives as null, a number can arrive as text, and codeUoM arrives as a nested object. A
 * strict !== reads every one of those as an edit and blocks a save nobody asked for.
 */
export function isLockedFieldUnchanged(key: ItemFieldKey, currentValue: any, requestedValue: any): boolean {

    switch (key) {

        case 'codeUoM':
            // hasOwnProperty passes for an explicitly-undefined codeUoM, so neither side is safe to deref.
            return (currentValue?.uom?._id ?? null) === (requestedValue?.uom?._id ?? null);

        case 'quantity':
        case 'buyPrice':
        case 'sellPrice':
            // Prices and quantities are stored as numbers but can arrive as numeric text from a form.
            return Number(currentValue ?? 0) === Number(requestedValue ?? 0);

        case 'suppliedBySubcontractor':
        case 'payable':
        case 'customSellPrice':
        case 'customBuyPrice':
            // An unset flag and an explicit false are the same line, and forms default these to false.
            // The custom-price flags have no schema default, so a stored line legitimately lacks them
            // while the editor posts `false` — reading that as an edit refuses a save nobody made.
            return (currentValue ?? false) === (requestedValue ?? false);

        default:
            // The string-ish fields: an absent key and an empty value both mean "nothing here".
            return (currentValue ?? null) === (requestedValue ?? null);
    }
}

/** Why a patch was refused, so the client can name the scope a user has to unmark. */
export interface FinanceLockRefusal {
    side: FinanceMarkSide;
    source: 'job' | 'stage';
    fields: ItemFieldKey[];
}

/**
 * The first side whose forced fields this patch tries to change, or null when the patch is allowed.
 *
 * Re-sending a value the line already holds is not an edit: the editor posts the whole item back, so
 * refusing an unchanged field would block every save on a marked line rather than the edit itself.
 */
export function resolveFinanceLockRefusal(beforeItem: EmbeddedCodeLike, patch: Partial<EmbeddedCodeLike>, lock: ItemFinanceLock | undefined): FinanceLockRefusal | null {

    for (const side of ['billable', 'payable'] as FinanceMarkSide[]) {

        const source = lock?.[side];
        if (!source || source === 'free') continue;

        const fields = financeLockedFields[side].filter(field => field in patch && !isLockedFieldUnchanged(field, beforeItem?.[field], patch[field]));

        if (fields.length) return { side, source, fields };
    }

    return null;
}

/**
 * The one sentence a refused write reports, wherever it was refused.
 *
 * Two guards raise it — the domain update commands and the shared item write path — and a user who
 * met the same wall twice with two different wordings would think they were two different rules.
 */
export function financeLockRefusalMessage(refusal: FinanceLockRefusal): string {

    const answer = refusal.side === 'billable' ? 'Not Billable' : 'Not Payable';

    return `This line is ${answer} because the ${refusal.source} is marked. Remove the mark on the ${refusal.source} to change it.`;
}
